import {
  BookOpen,
  Boxes,
  CalendarDays,
  CalendarRange,
  Compass,
  FileText,
  GitBranch,
  Grid3x3,
  HelpCircle,
  KanbanSquare,
  Layers,
  LayoutGrid,
  ListTodo,
  MessageSquarePlus,
  Milestone,
  Network,
  ScrollText,
  Sliders,
  Triangle,
  Users,
  Workflow,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { Workspace, type WorkspaceId } from "@/presentation/data/workspaces";

export interface SidebarItem {
  label: string;
  to: string;
  icon: LucideIcon;
  enabledFromWeek?: 1 | 2 | 3 | 4;
  /** Match this path only (ignores trailing slash) — no `startsWith` for child routes */
  exact?: boolean;
  /** Only shown to admin users (profiles.is_admin) — e.g. the Users page (BF-215). */
  adminOnly?: boolean;
}

export interface SidebarGroup {
  label: string;
  /** which workspace this group belongs to */
  workspace: WorkspaceId;
  tone?: "default" | "emphasis";
  items: SidebarItem[];
}

export const sidebarGroups: SidebarGroup[] = [
  // --- Implementation workspace: the lean build-phase view ------------------
  {
    label: "Delivery",
    workspace: Workspace.IMPLEMENTATION,
    items: [
      { label: "Timeline", to: "/timeline", icon: CalendarDays },
      { label: "MVP Delivery Board", to: "/board", icon: KanbanSquare },
      { label: "Build Planner", to: "/planner", icon: CalendarRange },
      // Sprint-grouped delivery tasks, with a block as an optional tag
      // (migrations 0129/0130) — admin-only, DB-enforced.
      { label: "Tasks", to: "/delivery-tasks", icon: ListTodo, adminOnly: true },
      { label: "Feedback", to: "/feedback", icon: MessageSquarePlus },
      { label: "Docs", to: "/docs", icon: BookOpen },
    ],
  },
  // --- B2C implementation workspace: the B2C MVP, run in marathons -----------
  {
    label: "B2C delivery",
    workspace: Workspace.B2C_IMPLEMENTATION,
    items: [
      // exact: /b2c/feedback lives under /b2c, so a prefix match would light both up.
      { label: "B2C Board", to: "/b2c", icon: Workflow, exact: true },
      // The same feedback list as /feedback (BF-1440).
      { label: "Feedback", to: "/b2c/feedback", icon: MessageSquarePlus },
    ],
  },
  {
    label: "Admin",
    workspace: Workspace.IMPLEMENTATION,
    items: [{ label: "Users", to: "/users", icon: Users, adminOnly: true }],
  },
  // --- Assessment workspace: everything from the assessment phase ------------
  {
    label: "This week",
    workspace: Workspace.ASSESSMENT,
    items: [
      { label: "Dashboard", to: "/", icon: LayoutGrid },
      { label: "Timeline 2.0", to: "/timeline-v2", icon: Milestone },
      { label: "Timeline 3.0", to: "/timeline-v3", icon: Milestone },
    ],
  },
  {
    label: "Track",
    workspace: Workspace.ASSESSMENT,
    items: [
      { label: "Meetings", to: "/meetings", icon: FileText },
      { label: "Decisions", to: "/decisions", icon: ScrollText },
      { label: "Risks", to: "/risks", icon: Triangle },
      { label: "Tasks", to: "/tasks", icon: ListTodo },
      { label: "Open Questions", to: "/open-questions", icon: HelpCircle },
    ],
  },
  {
    label: "Context",
    workspace: Workspace.ASSESSMENT,
    items: [
      { label: "Stakeholders", to: "/stakeholders", icon: Users, enabledFromWeek: 2 },
      { label: "Architecture map", to: "/architecture/map", icon: Network },
      { label: "All Features", to: "/roadmaps/all-features", icon: Layers },
      { label: "MVP board", to: "/roadmaps/from-scratch", icon: GitBranch },
      { label: "Products", to: "/products", icon: Boxes, enabledFromWeek: 3 },
      { label: "Candidates", to: "/candidates", icon: Compass, enabledFromWeek: 3 },
      { label: "Coverage matrix", to: "/coverage-matrix", icon: Grid3x3, enabledFromWeek: 3 },
      {
        label: "Evaluation criteria",
        to: "/evaluation-criteria",
        icon: Sliders,
        enabledFromWeek: 3,
      },
    ],
  },
];

/**
 * Resolve which workspace a route path belongs to, by longest-prefix match
 * against the sidebar items. Returns null for paths not owned by any workspace
 * (e.g. the dynamic /discoveries/* and /meetings/* detail routes are covered by
 * their /meetings ancestor; truly unknown paths return null and aren't guarded).
 */
export function routeWorkspace(pathname: string): WorkspaceId | null {
  const norm = pathname.replace(/\/$/, "") || "/";
  // /roadmaps and its nested /roadmaps/$branch routes belong to the assessment
  // workspace. The sidebar only links specific /roadmaps/* children, so without
  // this the parent + unlisted branches would be treated as unassigned and the
  // guard would let them through.
  if (norm === "/roadmaps" || norm.startsWith("/roadmaps/")) return Workspace.ASSESSMENT;
  let best: { workspace: WorkspaceId; len: number } | null = null;
  for (const group of sidebarGroups) {
    for (const item of group.items) {
      const to = item.to.replace(/\/$/, "") || "/";
      const matches = to === "/" ? norm === "/" : norm === to || norm.startsWith(`${to}/`);
      if (matches && (!best || to.length > best.len)) {
        best = { workspace: group.workspace, len: to.length };
      }
    }
  }
  // /discoveries/* lives in the assessment Context group via the dynamic submenu
  if (!best && norm.startsWith("/discoveries")) return Workspace.ASSESSMENT;
  return best?.workspace ?? null;
}

/** The landing route for a workspace — the first item of its first group. */
export function workspaceHome(workspace: WorkspaceId): string {
  for (const group of sidebarGroups) {
    const first = group.workspace === workspace ? group.items[0] : undefined;
    if (first) return first.to;
  }
  return "/";
}
